import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Alert,
  Avatar,
  AvatarGroup,
  Divider,
  Stack,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme,
  Slide,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Menu,
  MenuItem,
  Drawer,
  Switch,
  Fade,
  Snackbar,
} from '@mui/material';
import {
  Inventory as EquipmentIcon,
  FlashOn as FocusIcon,
  LocationOn as SitesIcon,
  Groups as TeamIcon,
  PlayArrow as ClockInIcon,
  Stop as ClockOutIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronIcon,
  Download as DownloadIcon,
  QrCodeScanner as ScanIcon,
  Description as ReportIcon,
  AddBox as ReceiveIcon,
  Circle as StatusDot,
  Circle,
  Schedule as TimelineIcon,
  History as ActivityIcon,
  Folder as FilesIcon,
  ErrorOutline as IssueIcon,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Search as SearchIcon,
  ArrowBack as BackIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  UnfoldMore as ExpandIcon,
  UnfoldLess as CollapseIcon,
  PushPin as PinIcon,
  Description as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  TableChart as SpreadsheetIcon,
  InsertDriveFile as GoogleDocsIcon,
  GridOn as GoogleSheetsIcon,
  Folder as GoogleDriveIcon,
  Link as LinkIcon,
  MoreVert as MoreIcon,
  Visibility as PreviewIcon,
  OpenInNew as OpenInNewIcon,
  Construction as InstallerIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Assignment as TaskIcon,
  SwapHoriz as ConvertIcon,
  Business as ExternalIcon,
  Home as InternalIcon,
  AccountTree as HierarchyIcon,
  Block as BlockedTaskIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import JobEquipmentPlanning from '../JobEquipmentPlanning';
import TaskDashboard from './TaskDashboard';

// Mock data - using existing equipment statuses
const mockEquipmentData = {
  total: 15,
  planned: 15,
  ordered: 2,
  received: 8,
  assigned: 6,
  installed: 4,
  onSite: 10,
  missing: 0,
  categories: [
    { name: 'Cameras', received: 6, total: 10 },
    { name: 'Mounts', received: 1, total: 3 },
    { name: 'Cables', received: 1, total: 2 },
  ],
  items: [
    { id: 'CAM-201', name: 'Dome Camera HD', status: 'Installed', location: 'Room 201', category: 'Cameras' },
    { id: 'CAM-202', name: 'Dome Camera HD', status: 'Installed', location: 'Room 202', category: 'Cameras' },
    { id: 'CAM-203', name: 'Dome Camera HD', status: 'Assigned', location: 'Storage', category: 'Cameras' },
    { id: 'CAM-204', name: 'Dome Camera HD', status: 'Assigned', location: 'Storage', category: 'Cameras' },
    { id: 'CAM-205', name: 'Bullet Camera HD', status: 'Received', location: 'Warehouse', category: 'Cameras' },
    { id: 'CAM-206', name: 'Bullet Camera HD', status: 'Received', location: 'Warehouse', category: 'Cameras' },
    { id: 'CAM-207', name: 'PTZ Camera', status: 'Installed', location: 'Gymnasium', category: 'Cameras' },
    { id: 'CAM-208', name: 'PTZ Camera', status: 'Assigned', location: 'Storage', category: 'Cameras' },
    { id: 'CAM-209', name: 'Dome Camera HD', status: 'Ordered', location: 'In Transit', category: 'Cameras' },
    { id: 'CAM-210', name: 'Dome Camera HD', status: 'Received', location: 'Warehouse', category: 'Cameras' },
    { id: 'MNT-101', name: 'Ceiling Mount', status: 'Received', location: 'Warehouse', category: 'Mounts' },
    { id: 'MNT-102', name: 'Wall Mount', status: 'Assigned', location: 'Storage', category: 'Mounts' },
    { id: 'MNT-103', name: 'Pole Mount', status: 'Ordered', location: 'In Transit', category: 'Mounts' },
    { id: 'CBL-001', name: 'Cat6 Cable 1000ft', status: 'Received', location: 'IDF', category: 'Cables' },
    { id: 'CBL-002', name: 'Power Cable 500ft', status: 'Installed', location: 'IDF', category: 'Cables' },
  ]
};

const mockSites = [
  { id: 1, name: 'Building A', progress: 80, installed: 20, total: 25 },
  { id: 2, name: 'Building B', progress: 40, installed: 8, total: 20 },
  { id: 3, name: 'Gymnasium', progress: 100, installed: 5, total: 5, complete: true },
];

const mockTeam = [
  { id: 1, name: 'Matt', status: 'on-site', activity: 'Installing Rm 201', avatar: 'M', color: '#9c27b0' },
  { id: 2, name: 'Clayton', status: 'on-site', activity: 'Running cable', avatar: 'C', color: '#607d8b' },
  { id: 3, name: 'Dave', status: 'in-transit', activity: 'ETA 10:30am', avatar: 'D', color: '#e91e63' },
];

const mockMilestones = [
  { id: 1, title: 'Equipment ordered', date: 'Sep 1', complete: true },
  { id: 2, title: 'Permits approved', date: 'Sep 10', complete: true },
  { id: 3, title: 'Site prep complete', date: 'Sep 14', complete: true },
  { id: 4, title: 'Camera installation', date: 'NOW', progress: 65, active: true },
  { id: 5, title: 'Network configuration', date: 'Oct 15-20', complete: false },
  { id: 6, title: 'Testing & commissioning', date: 'Oct 22-28', complete: false },
  { id: 7, title: 'Training & handover', date: 'Oct 29', complete: false },
];

const mockActivities = [
  { time: '10:15 AM', text: 'Matt installed camera CAM-201 in Room 201' },
  { time: '10:02 AM', text: 'Clayton completed cable run for Building A' },
  { time: '9:45 AM', text: '5 mounts marked as delayed (vendor issue)', type: 'warning' },
  { time: '9:30 AM', text: 'Dave clocked in' },
];

const mockDocuments = [
  { id: 1, name: 'Installation Plans v3', type: 'pdf', size: '2.4 MB', date: 'Sep 1, 2024', pinned: true, url: '/demo-files/installation-plans.pdf' },
  { id: 2, name: 'Site Survey Photos', type: 'google-drive', size: 'Google Drive', date: 'Aug 28, 2024', pinned: true, url: 'https://drive.google.com/drive/folders/1ABC123def456' },
  { id: 3, name: 'Equipment Specs', type: 'google-sheets', size: 'Google Sheets', date: 'Aug 25, 2024', pinned: true, url: 'https://docs.google.com/spreadsheets/d/1XYZ789abc123' },
  { id: 4, name: 'Contract Signed', type: 'google-docs', size: 'Google Docs', date: 'Aug 15, 2024', pinned: true, url: 'https://docs.google.com/document/d/1DEF456ghi789' },
  { id: 5, name: 'Network_Diagram.png', type: 'image', size: '1.2 MB', date: 'Sep 3, 2024', pinned: true, url: '/demo-files/network.png' },
  { id: 6, name: 'Floor Plans', type: 'pdf', size: '3.8 MB', date: 'Aug 20, 2024', pinned: true, url: '/demo-files/floor-plans.pdf' },
  { id: 7, name: 'Budget Overview', type: 'google-sheets', size: 'Google Sheets', date: 'Aug 22, 2024', pinned: true, url: 'https://docs.google.com/spreadsheets/d/1GHI012jkl345' },
  { id: 8, name: 'Safety Procedures', type: 'google-docs', size: 'Google Docs', date: 'Aug 20, 2024', pinned: true, url: 'https://docs.google.com/document/d/1JKL678mno901' },
  { id: 9, name: 'Change_Order_001.pdf', type: 'pdf', size: '423 KB', date: 'Sep 5, 2024', pinned: false, url: '/demo-files/change-order.pdf' },
  { id: 10, name: 'Camera_Test_Results.xlsx', type: 'spreadsheet', size: '234 KB', date: 'Sep 14, 2024', pinned: false, url: '/demo-files/test-results.xlsx' },
  { id: 11, name: 'Training_Materials.pdf', type: 'pdf', size: '5.1 MB', date: 'Sep 10, 2024', pinned: false, url: '/demo-files/training.pdf' },
  { id: 12, name: 'Warranty_Documentation.pdf', type: 'pdf', size: '892 KB', date: 'Sep 12, 2024', pinned: false, url: '/demo-files/warranty.pdf' },
  { id: 13, name: 'Project_Schedule.xlsx', type: 'spreadsheet', size: '145 KB', date: 'Aug 30, 2024', pinned: false, url: '/demo-files/schedule.xlsx' },
  { id: 14, name: 'Client_Approval_Forms.pdf', type: 'pdf', size: '567 KB', date: 'Sep 8, 2024', pinned: false, url: '/demo-files/approvals.pdf' },
];

const mockIssues = [
  {
    id: 1,
    title: '5 camera mounts delayed',
    severity: 'medium',
    status: 'open',
    date: 'Today',
    description: 'Vendor shipping delay, expected tomorrow',
    type: 'external', // External dependency on vendor
    convertedToTask: false
  },
  {
    id: 2,
    title: 'Network switch compatibility',
    severity: 'low',
    status: 'resolved',
    date: 'Yesterday',
    description: 'Resolved by updating firmware',
    type: 'internal',
    convertedToTask: true
  },
  {
    id: 3,
    title: 'Building B access restricted',
    severity: 'high',
    status: 'open',
    date: '2 days ago',
    description: 'Waiting for security clearance from customer',
    type: 'external', // External - needs customer action
    convertedToTask: false
  },
  {
    id: 4,
    title: 'Cable pathway obstruction in Room 205',
    severity: 'medium',
    status: 'open',
    date: 'Today',
    description: 'Need to reroute cables due to HVAC ductwork',
    type: 'internal',
    convertedToTask: false
  },
];

export default function JobProjectView({ job }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isClocked, setIsClocked] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const intervalRef = useRef(null);

  // Role-based view state - default to PM/Admin view
  const [userRole, setUserRole] = useState('admin'); // 'admin' or 'installer'
  const [showRoleToggle, setShowRoleToggle] = useState(false);

  const [expandedSections, setExpandedSections] = useState({
    timeline: true,
    activity: true,
    documents: false,
    issues: false,
  });

  // Three-level interaction states
  const [equipmentCardExpanded, setEquipmentCardExpanded] = useState(false);
  const [equipmentModalOpen, setEquipmentModalOpen] = useState(false);
  const [equipmentFilter, setEquipmentFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [tasksExpanded, setTasksExpanded] = useState(false);

  // Document preview states
  const [showAllDocs, setShowAllDocs] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [docMenuAnchor, setDocMenuAnchor] = useState(null);

  // Milestone CRUD states
  const [milestones, setMilestones] = useState(mockMilestones);
  const [milestoneDialog, setMilestoneDialog] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [milestoneForm, setMilestoneForm] = useState({
    title: '',
    date: '',
    complete: false
  });

  // Snackbar state for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    if (isClocked && startTime) {
      intervalRef.current = setInterval(() => {
        setElapsedTime(Date.now() - startTime);
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isClocked, startTime]);

  const handleClockIn = () => {
    setIsClocked(true);
    setStartTime(Date.now());
  };

  const handleClockOut = () => {
    setIsClocked(false);
    setElapsedTime(0);
    setStartTime(null);
  };

  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    return `${hours}:${String(minutes % 60).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Milestone CRUD handlers
  const handleAddMilestone = () => {
    setEditingMilestone(null);
    setMilestoneForm({ title: '', date: '', complete: false });
    setMilestoneDialog(true);
  };

  const handleEditMilestone = (milestone) => {
    setEditingMilestone(milestone.id);
    setMilestoneForm({
      title: milestone.title,
      date: milestone.date,
      complete: milestone.complete
    });
    setMilestoneDialog(true);
  };

  const handleSaveMilestone = () => {
    if (editingMilestone) {
      // Edit existing
      setMilestones(prev => prev.map(m =>
        m.id === editingMilestone
          ? { ...m, ...milestoneForm }
          : m
      ));
    } else {
      // Add new
      setMilestones(prev => [...prev, {
        id: Math.max(...prev.map(m => m.id)) + 1,
        ...milestoneForm,
        progress: milestoneForm.complete ? 100 : 0,
        active: false
      }]);
    }
    setMilestoneDialog(false);
    setMilestoneForm({ title: '', date: '', complete: false });
  };

  const handleDeleteMilestone = (id) => {
    setMilestones(prev => prev.filter(m => m.id !== id));
  };

  const handleToggleMilestoneComplete = (id) => {
    setMilestones(prev => prev.map(m =>
      m.id === id
        ? { ...m, complete: !m.complete, progress: !m.complete ? 100 : 0 }
        : m
    ));
  };

  // Helper to check if feature should be visible based on role
  const isAdminOnly = (feature) => {
    const adminFeatures = ['budget', 'hours', 'team', 'equipment', 'sites', 'issues', 'milestones', 'activity'];
    return adminFeatures.includes(feature) && userRole === 'installer';
  };

  const handleRoleToggle = (event, newRole) => {
    if (newRole !== null) {
      setUserRole(newRole);
    }
  };

  return (
    <Box>
      {/* Role Toggle (subtle but discoverable) */}
      <Paper
        sx={{
          p: 1,
          mb: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: userRole === 'installer' ? 'primary.main' : 'warning.main',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderWidth: 2,
          }
        }}
        onMouseEnter={() => setShowRoleToggle(true)}
        onMouseLeave={() => setShowRoleToggle(false)}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          opacity: showRoleToggle || userRole === 'installer' ? 1 : 0.7,
          transition: 'opacity 0.3s ease'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Demo View Mode:
            </Typography>
            <ToggleButtonGroup
              value={userRole}
              exclusive
              onChange={handleRoleToggle}
              size="small"
              sx={{ height: 28 }}
            >
              <ToggleButton value="installer" sx={{ px: 1.5, py: 0.5 }}>
                <InstallerIcon sx={{ fontSize: 16, mr: 0.5 }} />
                Installer
              </ToggleButton>
              <ToggleButton value="admin" sx={{ px: 1.5, py: 0.5 }}>
                <AdminIcon sx={{ fontSize: 16, mr: 0.5 }} />
                PM/Admin
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Chip
            size="small"
            icon={<PersonIcon sx={{ fontSize: 14 }} />}
            label={userRole === 'installer' ? 'Matt (Installer View)' : 'Eric (Project Manager View)'}
            color={userRole === 'installer' ? 'primary' : 'warning'}
            sx={{ height: 24, fontSize: '0.75rem' }}
          />
        </Box>
      </Paper>

      {/* Clean Header */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {job?.jobNumber || 'J2024-087'}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {job?.name || 'Noblesville High School Cameras PLTW'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip label={job?.pm || 'Clayton'} color="success" sx={{ px: 2 }} />
            <Chip label={job?.status || 'Active'} variant="outlined" sx={{ px: 2 }} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mt: 3 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
              Timeline
            </Typography>
            <Typography variant="body2">
              Sep 14 - Oct 29
            </Typography>
          </Box>
          {userRole === 'admin' && (
            <>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  Budget
                </Typography>
                <Typography variant="body2">
                  {job?.actualHours || 52}/{job?.budgetHours || 80} hrs (65%)
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  Team On-Site
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
                    <Avatar sx={{ bgcolor: '#9c27b0' }}>M</Avatar>
                    <Avatar sx={{ bgcolor: '#607d8b' }}>C</Avatar>
                    <Avatar sx={{ bgcolor: '#e91e63' }}>D</Avatar>
                  </AvatarGroup>
                  <Typography variant="body2" color="text.secondary">
                    3 installers • Updated 2 min ago
                  </Typography>
                </Box>
              </Box>
            </>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <Button
              variant="contained"
              color="success"
              onClick={isClocked ? handleClockOut : handleClockIn}
              startIcon={isClocked ? <ClockOutIcon /> : <ClockInIcon />}
              sx={{ minWidth: 120 }}
            >
              {isClocked ? 'Clock Out' : 'Clock In'}
            </Button>
            {isClocked && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                bgcolor: 'success.50',
                borderRadius: 1,
                border: 1,
                borderColor: 'success.200'
              }}>
                <Box sx={{
                  width: 8,
                  height: 8,
                  bgcolor: 'success.main',
                  borderRadius: '50%',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.3 },
                    '100%': { opacity: 1 }
                  },
                  animation: 'pulse 2s infinite'
                }} />
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                  {formatTime(elapsedTime)}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Pinned Files in Header - Mobile-optimized with better visibility */}
        {mockDocuments.filter(d => d.pinned).length > 0 && (
          <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PinIcon sx={{ fontSize: { xs: 20, sm: 18 }, color: 'text.secondary' }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    textTransform: 'uppercase',
                    fontSize: { xs: '0.8rem', sm: '0.7rem' },
                    fontWeight: 500
                  }}
                >
                  Quick Access ({mockDocuments.filter(d => d.pinned).length} files)
                </Typography>
              </Box>
              {mockDocuments.filter(d => d.pinned).length > (isMobile ? 2 : 4) && (
                <Button
                  size="small"
                  onClick={() => setShowAllDocs(!showAllDocs)}
                  endIcon={showAllDocs ? <CollapseIcon sx={{ fontSize: 16 }} /> : <ExpandIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    fontSize: { xs: '0.8rem', sm: '0.75rem' },
                    py: 0.5,
                    minWidth: 'auto'
                  }}
                >
                  {showAllDocs ? 'Show Less' : 'Show All'}
                </Button>
              )}
            </Box>

            {/* Mobile-optimized grid layout */}
            <Box sx={{
              display: isMobile ? 'grid' : 'flex',
              gridTemplateColumns: isMobile ? '1fr 1fr' : undefined,
              gap: { xs: 1.5, sm: 1 },
              flexWrap: !isMobile ? 'wrap' : undefined,
              maxHeight: showAllDocs ? 'none' : (isMobile ? 'none' : '80px'),
              overflow: 'hidden',
              position: 'relative',
              transition: 'max-height 0.3s ease'
            }}>
              {mockDocuments.filter(d => d.pinned).slice(0, showAllDocs ? undefined : (isMobile ? 4 : 4)).map(doc => {
                let icon = <FileIcon sx={{ fontSize: { xs: 20, sm: 16 } }} />;
                let isGoogleDoc = false;

                // Icons based on file type
                if (doc.type === 'pdf') icon = <PdfIcon sx={{ fontSize: { xs: 20, sm: 16 }, color: '#d32f2f' }} />;
                else if (doc.type === 'image') icon = <ImageIcon sx={{ fontSize: { xs: 20, sm: 16 }, color: '#1976d2' }} />;
                else if (doc.type === 'spreadsheet') icon = <SpreadsheetIcon sx={{ fontSize: { xs: 20, sm: 16 }, color: '#2e7d32' }} />;
                else if (doc.type === 'google-docs') {
                  icon = <GoogleDocsIcon sx={{ fontSize: { xs: 20, sm: 16 }, color: '#4285f4' }} />;
                  isGoogleDoc = true;
                }
                else if (doc.type === 'google-sheets') {
                  icon = <GoogleSheetsIcon sx={{ fontSize: { xs: 20, sm: 16 }, color: '#0f9d58' }} />;
                  isGoogleDoc = true;
                }
                else if (doc.type === 'google-drive') {
                  icon = <GoogleDriveIcon sx={{ fontSize: { xs: 20, sm: 16 }, color: '#4285f4' }} />;
                  isGoogleDoc = true;
                }

                return (
                  <Paper
                    key={doc.id}
                    elevation={0}
                    sx={{
                      p: { xs: 1.5, sm: 1 },
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        borderColor: 'primary.main',
                        transform: 'translateY(-1px)',
                        boxShadow: 1
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      }
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      // Open Google Drive links directly in new tab
                      if (isGoogleDoc && doc.url.startsWith('http')) {
                        window.open(doc.url, '_blank');
                      } else if (doc.type === 'pdf' || doc.type === 'image') {
                        // For PDFs and images, open in preview modal
                        setPreviewDoc(doc);
                        setPreviewDialog(true);
                      } else {
                        // Show menu for other file types
                        setDocMenuAnchor(e.currentTarget);
                        setPreviewDoc(doc);
                      }
                    }}
                  >
                    {icon}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: { xs: '0.875rem', sm: '0.8rem' },
                          fontWeight: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          lineHeight: 1.2
                        }}
                      >
                        {doc.name.replace(/_/g, ' ').replace('.pdf', '').replace('.xlsx', '')}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: { xs: '0.7rem', sm: '0.65rem' },
                          color: 'text.secondary',
                          display: { xs: 'block', sm: 'none' }
                        }}
                      >
                        {doc.size}
                        {isGoogleDoc && (
                          <OpenInNewIcon sx={{ fontSize: { xs: 12, sm: 10 }, ml: 0.5, verticalAlign: 'middle' }} />
                        )}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      sx={{
                        p: 0.5,
                        display: { xs: 'flex', sm: 'none' }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isGoogleDoc && doc.url.startsWith('http')) {
                          window.open(doc.url, '_blank');
                        } else if (doc.type === 'pdf' || doc.type === 'image') {
                          setPreviewDoc(doc);
                          setPreviewDialog(true);
                        } else if (doc.url) {
                          window.open(doc.url, '_blank');
                        }
                      }}
                    >
                      {isGoogleDoc || doc.type === 'pdf' || doc.type === 'image' ?
                        <OpenInNewIcon sx={{ fontSize: 18 }} /> :
                        <DownloadIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </Paper>
                );
              })}

              {/* Show more indicator for mobile */}
              {!showAllDocs && mockDocuments.filter(d => d.pinned).length > (isMobile ? 4 : 4) && (
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 1.5, sm: 1 },
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    gridColumn: isMobile ? 'span 2' : undefined,
                    '&:hover': {
                      bgcolor: 'grey.200'
                    }
                  }}
                  onClick={() => setShowAllDocs(true)}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '0.8rem' },
                      color: 'text.secondary',
                      fontWeight: 500
                    }}
                  >
                    +{mockDocuments.filter(d => d.pinned).length - (isMobile ? 4 : 4)} more files
                  </Typography>
                </Paper>
              )}
            </Box>
          </Box>
        )}
      </Paper>


      {/* Installer View - Simplified Task Dashboard */}
      {userRole === 'installer' && (
        <Box sx={{ mb: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TaskIcon color="primary" /> My Tasks for Today
              </Typography>
              <Stack spacing={1}>
                <FormControlLabel
                  control={<Checkbox defaultChecked />}
                  label="Install camera CAM-203 in Room 203"
                />
                <FormControlLabel
                  control={<Checkbox defaultChecked />}
                  label="Install camera CAM-204 in Room 204"
                />
                <FormControlLabel
                  control={<Checkbox />}
                  label="Run cable from IDF to Room 205"
                />
                <FormControlLabel
                  control={<Checkbox />}
                  label="Mount and configure CAM-205"
                />
                <FormControlLabel
                  control={<Checkbox />}
                  label="Test network connectivity for all new cameras"
                />
              </Stack>
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                  Progress: 2/5 tasks completed
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={40}
                  sx={{ mt: 1, height: 6, borderRadius: 3 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Main Dashboard Cards */}
      <Box sx={{
        display: 'flex',
        gap: 2,
        mb: 3,
        flexWrap: 'nowrap',
        overflowX: 'auto'
      }}>
        {/* Equipment Card - Three Level Interaction (Admin Only) */}
        {userRole === 'admin' && (
          <Box sx={{ flex: '1 1 25%', minWidth: '280px', display: 'flex' }}>
            <Card
            sx={{
              width: '100%',
              height: '100%',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-2px)',
              }
            }}
            onClick={() => setEquipmentModalOpen(true)}
          >
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EquipmentIcon /> Equipment
                </Typography>
              </Box>

              {/* Progress Bar */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">{mockEquipmentData.received}/{mockEquipmentData.total}</Typography>
                  <Typography variant="body2" color="text.secondary">{Math.round((mockEquipmentData.received / mockEquipmentData.total) * 100)}%</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(mockEquipmentData.received / mockEquipmentData.total) * 100}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              {/* Status Summary - All Statuses */}
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: 'grey.400', borderRadius: '2px' }} />
                    <Typography variant="body2" fontSize="0.875rem">Planned</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="medium" fontSize="0.875rem" sx={{ whiteSpace: 'nowrap' }}>{mockEquipmentData.planned} items</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: 'warning.main', borderRadius: '2px' }} />
                    <Typography variant="body2" fontSize="0.875rem">Ordered</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="medium" fontSize="0.875rem" sx={{ whiteSpace: 'nowrap' }}>{mockEquipmentData.ordered} items</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: 'info.main', borderRadius: '2px' }} />
                    <Typography variant="body2" fontSize="0.875rem">Received</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="medium" fontSize="0.875rem" sx={{ whiteSpace: 'nowrap' }}>{mockEquipmentData.received} items</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: 'primary.main', borderRadius: '2px' }} />
                    <Typography variant="body2" fontSize="0.875rem">Assigned</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="medium" fontSize="0.875rem" sx={{ whiteSpace: 'nowrap' }}>{mockEquipmentData.assigned} items</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                    <Box sx={{ width: 10, height: 10, bgcolor: 'success.main', borderRadius: '2px' }} />
                    <Typography variant="body2" fontSize="0.875rem">Installed</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="medium" fontSize="0.875rem" sx={{ whiteSpace: 'nowrap' }}>{mockEquipmentData.installed} items</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
        )}

        {/* Task Management Card - Opens slide-out panel */}
        <Box sx={{ flex: '1 1 25%', minWidth: '280px', display: 'flex' }}>
          <Card
            sx={{
              width: '100%',
              height: '100%',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-2px)',
              }
            }}
            onClick={() => setTasksExpanded(true)}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ActivityIcon sx={{ color: 'info.main' }} />
                <Typography variant="h6">Planning Activity</Typography>
                <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                  <Chip label="3 recent" color="info" size="small" />
                  <Chip label="Live updates" color="success" size="small" variant="outlined" />
                </Box>
              </Box>

              {/* Task Summary Preview */}
              <List dense sx={{ mb: 2 }}>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Site Preparation"
                    secondary="Mike • Completed • 4 subtasks done"
                  />
                </ListItem>
                <ListItem sx={{ px: 0, bgcolor: 'info.50', borderRadius: 1 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Circle sx={{ color: 'info.main', fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Install Cameras - Building A"
                    secondary="Clayton • 60% complete • 15/25 cameras"
                  />
                  <Chip label="In Progress" size="small" color="info" />
                </ListItem>
                <ListItem sx={{ px: 0, bgcolor: 'error.50', borderRadius: 1 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <WarningIcon sx={{ color: 'error.main', fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Network Configuration"
                    secondary="David • Blocked - Switch firmware issue"
                  />
                  <Chip label="Blocked" size="small" color="error" />
                </ListItem>
              </List>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">
                  Total: 5 parent tasks, 23 subtasks
                </Typography>
                <Button
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTasksExpanded(true);
                  }}
                >
                  View All
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Sites & Progress (Admin Only) */}
        {userRole === 'admin' && (
          <Box sx={{ flex: '1 1 25%', minWidth: '280px', display: 'flex' }}>
            <Card sx={{ width: '100%', height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <SitesIcon /> Sites & Progress
                </Typography>
                <Stack spacing={2}>
                {mockSites.map(site => (
                  <Box key={site.id}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {site.name}
                        {site.complete && <CheckIcon sx={{ ml: 1, fontSize: 16, color: 'success.main' }} />}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {site.progress}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={site.progress}
                      color={site.complete ? 'success' : 'primary'}
                      sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      • {site.installed}/{site.total} cameras installed
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Box>
        )}

        {/* Team Status (Admin Only) */}
        {userRole === 'admin' && (
          <Box sx={{ flex: '1 1 25%', minWidth: '280px', display: 'flex' }}>
            <Card sx={{ width: '100%', height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <TeamIcon /> Team Status
                </Typography>
                <Stack spacing={2}>
                {mockTeam.map(member => (
                  <Box key={member.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: member.color, fontSize: '0.875rem' }}>
                      {member.avatar}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight="medium">{member.name}</Typography>
                        <StatusDot
                          sx={{
                            fontSize: 8,
                            color: member.status === 'on-site' ? 'success.main' : 'info.main'
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {member.status === 'on-site' ? 'On-site' : 'In transit'}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {member.activity}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Box>
        )}
      </Box>

      {/* Collapsible Sections */}
      <Stack spacing={2}>
        {/* Timeline & Milestones (Admin Only) */}
        {userRole === 'admin' && (
          <Paper>
            <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' }
            }}
            onClick={() => toggleSection('timeline')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {expandedSections.timeline ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                <TimelineIcon /> Project Timeline & Milestones
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddMilestone();
                }}
                sx={{ mr: 1 }}
              >
                <AddIcon />
              </IconButton>
            </Box>
          </Box>
          <Collapse in={expandedSections.timeline}>
            <Divider />
            <Box sx={{ p: 2 }}>
              {/* Timeline Bar */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption">Sep 14</Typography>
                  <Typography variant="caption">Oct 29</Typography>
                </Box>
                <Box sx={{ position: 'relative', height: 8, bgcolor: 'grey.200', borderRadius: 4 }}>
                  <Box sx={{ position: 'absolute', left: 0, top: 0, width: '65%', height: '100%', bgcolor: 'primary.main', borderRadius: 4 }} />
                  <Box sx={{ position: 'absolute', left: '10%', top: '-4px', width: 16, height: 16, bgcolor: 'success.main', borderRadius: '50%', border: '2px solid white' }} />
                  <Box sx={{ position: 'absolute', left: '65%', top: '-4px', width: 16, height: 16, bgcolor: 'warning.main', borderRadius: '50%', border: '2px solid white' }} />
                  <Box sx={{ position: 'absolute', right: 0, top: '-4px', width: 16, height: 16, bgcolor: 'grey.400', borderRadius: '50%', border: '2px solid white' }} />
                </Box>
              </Box>

              {/* Milestones List */}
              <Stack spacing={1}>
                {milestones.map(milestone => (
                  <Box
                    key={milestone.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 1,
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                        '& .milestone-actions': {
                          opacity: 1
                        }
                      }
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleToggleMilestoneComplete(milestone.id)}
                      sx={{ p: 0.5 }}
                    >
                      {milestone.complete ? (
                        <CheckIcon sx={{ fontSize: 20, color: 'success.main' }} />
                      ) : milestone.active ? (
                        <Badge badgeContent={`${milestone.progress}%`} color="primary">
                          <StatusDot sx={{ fontSize: 20, color: 'warning.main' }} />
                        </Badge>
                      ) : (
                        <StatusDot sx={{ fontSize: 20, color: 'grey.400' }} />
                      )}
                    </IconButton>
                    <Typography variant="body2" sx={{ flex: 1, textDecoration: milestone.complete ? 'line-through' : 'none' }}>
                      {milestone.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {milestone.date}
                    </Typography>
                    <Box
                      className="milestone-actions"
                      sx={{
                        display: 'flex',
                        gap: 0.5,
                        opacity: 0,
                        transition: 'opacity 0.2s'
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleEditMilestone(milestone)}
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteMilestone(milestone.id)}
                        sx={{ p: 0.5 }}
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Collapse>
        </Paper>
        )}

        {/* Recent Activity (Admin Only) */}
        {userRole === 'admin' && (
          <Paper>
            <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' }
            }}
            onClick={() => toggleSection('activity')}
          >
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {expandedSections.activity ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
              <ActivityIcon /> Recent Activity
            </Typography>
          </Box>
          <Collapse in={expandedSections.activity}>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                {mockActivities.map((activity, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
                      {activity.time}
                    </Typography>
                    <Typography variant="body2" sx={{ color: activity.type === 'warning' ? 'warning.main' : 'text.primary' }}>
                      {activity.text}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Collapse>
        </Paper>
        )}

        {/* Documents & Files (Available to All Users) */}
        <Paper>
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' }
            }}
            onClick={() => toggleSection('documents')}
          >
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {expandedSections.documents ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
              <FilesIcon /> Documents & Files
              <Chip label="12" size="small" sx={{ ml: 1 }} />
            </Typography>
          </Box>
          <Collapse in={expandedSections.documents}>
            <Divider />
            <Box sx={{ p: 2 }}>
              <List dense>
                {mockDocuments.map(doc => {
                  let icon = <FileIcon />;
                  let isGoogleDoc = false;

                  // Icons based on file type
                  if (doc.type === 'pdf') icon = <PdfIcon sx={{ color: '#d32f2f' }} />;
                  else if (doc.type === 'image') icon = <ImageIcon sx={{ color: '#1976d2' }} />;
                  else if (doc.type === 'spreadsheet') icon = <SpreadsheetIcon sx={{ color: '#2e7d32' }} />;
                  else if (doc.type === 'google-docs') {
                    icon = <GoogleDocsIcon sx={{ color: '#4285f4' }} />;
                    isGoogleDoc = true;
                  }
                  else if (doc.type === 'google-sheets') {
                    icon = <GoogleSheetsIcon sx={{ color: '#0f9d58' }} />;
                    isGoogleDoc = true;
                  }
                  else if (doc.type === 'google-drive') {
                    icon = <GoogleDriveIcon sx={{ color: '#4285f4' }} />;
                    isGoogleDoc = true;
                  }

                  return (
                    <ListItem
                      key={doc.id}
                      button
                      onClick={() => {
                        if (isGoogleDoc && doc.url.startsWith('http')) {
                          window.open(doc.url, '_blank');
                        } else if (doc.type === 'pdf' || doc.type === 'image') {
                          // For PDFs and images, open in preview modal
                          setPreviewDoc(doc);
                          setPreviewDialog(true);
                        } else {
                          // For other files, show menu
                          setDocMenuAnchor(null);
                          setPreviewDoc(doc);
                        }
                      }}
                      sx={{
                        '&:hover': { bgcolor: 'action.hover' },
                        borderRadius: 1,
                        cursor: 'pointer',
                      }}
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          {doc.pinned && <PinIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          <IconButton
                            size="small"
                            edge="end"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isGoogleDoc && doc.url.startsWith('http')) {
                                window.open(doc.url, '_blank');
                              } else {
                                // Download for regular files
                                window.open(doc.url, '_blank');
                              }
                            }}
                          >
                            {isGoogleDoc ? <OpenInNewIcon fontSize="small" /> : <DownloadIcon fontSize="small" />}
                          </IconButton>
                        </Box>
                      }
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={doc.name}
                        secondary={`${doc.size} • ${doc.date}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Collapse>
        </Paper>

        {/* Issues & Notes (Admin Only) */}
        {userRole === 'admin' && (
          <Paper>
            <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' }
            }}
            onClick={() => toggleSection('issues')}
          >
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {expandedSections.issues ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
              <IssueIcon /> Issues & Notes
              <Chip label="3" size="small" color="warning" sx={{ ml: 1 }} />
            </Typography>
          </Box>
          <Collapse in={expandedSections.issues}>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Stack spacing={2}>
                {mockIssues.map(issue => (
                  <Box
                    key={issue.id}
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      bgcolor: issue.status === 'open' ? 'warning.50' : 'grey.50',
                      border: 1,
                      borderColor: issue.status === 'open' ? 'warning.main' : 'grey.300',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" fontWeight="medium">
                          {issue.title}
                        </Typography>
                        {/* External/Internal indicator */}
                        <Tooltip title={issue.type === 'external' ? 'External dependency' : 'Internal task'}>
                          {issue.type === 'external' ? (
                            <ExternalIcon sx={{ fontSize: 18, color: 'error.main' }} />
                          ) : (
                            <InternalIcon sx={{ fontSize: 18, color: 'info.main' }} />
                          )}
                        </Tooltip>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip
                          label={issue.severity}
                          size="small"
                          color={
                            issue.severity === 'high' ? 'error' :
                            issue.severity === 'medium' ? 'warning' :
                            'default'
                          }
                          variant="outlined"
                        />
                        <Chip
                          label={issue.status}
                          size="small"
                          color={issue.status === 'open' ? 'warning' : 'success'}
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {issue.description}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">
                        {issue.date}
                      </Typography>
                      {/* Convert to Task button */}
                      {issue.status === 'open' && !issue.convertedToTask && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ConvertIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            // In a real app, this would create a task from the issue
                            setSnackbar({
                              open: true,
                              message: `Issue "${issue.title}" converted to task successfully!`,
                              severity: 'success'
                            });
                            // Update the issue to show it's been converted
                            issue.convertedToTask = true;
                          }}
                          sx={{
                            fontSize: '0.75rem',
                            py: 0.5,
                            px: 1,
                            minHeight: 'auto'
                          }}
                        >
                          Convert to Task
                        </Button>
                      )}
                      {issue.convertedToTask && (
                        <Chip
                          label="Converted to Task"
                          size="small"
                          icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                          color="success"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 24 }}
                        />
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Collapse>
        </Paper>
        )}
      </Stack>

      {/* Equipment Planning Modal - Level 2 Interaction */}
      <Dialog
        open={equipmentModalOpen}
        onClose={() => setEquipmentModalOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={isMobile ? Slide : undefined}
        TransitionProps={isMobile ? { direction: 'left' } : undefined}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={() => setEquipmentModalOpen(false)} edge="start">
                <BackIcon />
              </IconButton>
              <Box>
                <Typography variant="h6">Equipment Planning</Typography>
                <Typography variant="caption" color="text.secondary">
                  {job?.jobNumber || 'J2024-087'} - {job?.name || 'Noblesville High School Cameras'}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={() => setEquipmentModalOpen(false)} edge="end">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Quick Stats Bar */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Chip label={`${mockEquipmentData.total} Total`} />
            <Chip label={`${mockEquipmentData.onSite} On-Site`} color="success" />
            <Chip label={`${mockEquipmentData.installed} Installed`} color="primary" />
            <Chip label={`${mockEquipmentData.missing} Missing`} color="error" />
          </Box>

          {/* Use the existing JobEquipmentPlanning component with mock data */}
          <JobEquipmentPlanning
            jobId={job?.id || 'pm-demo-job'}
            jobNumber={job?.jobNumber || 'J2024-087'}
            equipmentPlans={mockEquipmentData.items.map(item => ({
              id: item.id,
              job_id: 'pm-demo-job',
              template_id: item.id,
              quantity: 1,
              equipmentTemplate: {
                id: item.id,
                category: item.category,
                manufacturer: item.category === 'Cameras' ? 'Axis' : item.category === 'Mounts' ? 'Pelco' : 'Panduit',
                model: item.id,
                description: item.name,
              },
              equipmentItems: [{
                id: `${item.id}-001`,
                template_id: item.id,
                serial_number: `SN-${item.id}`,
                asset_tag: `AT-${item.id}`,
                status: item.status.toLowerCase(),
                location: item.location,
                condition: 'good',
                purchase_date: '2024-01-15',
                purchase_price: item.category === 'Cameras' ? 450 : item.category === 'Mounts' ? 75 : 50,
                warranty_expiration: '2027-01-15',
                notes: `${item.name} for ${item.location}`
              }]
            }))}
            onUpdate={() => {
              console.log('Equipment plan updated');
            }}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEquipmentModalOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Milestone Add/Edit Dialog */}
      <Dialog
        open={milestoneDialog}
        onClose={() => setMilestoneDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingMilestone ? 'Edit Milestone' : 'Add New Milestone'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Milestone Title"
              value={milestoneForm.title}
              onChange={(e) => setMilestoneForm(prev => ({ ...prev, title: e.target.value }))}
              fullWidth
              required
              placeholder="e.g., Equipment ordered, Site prep complete"
            />
            <TextField
              label="Date"
              value={milestoneForm.date}
              onChange={(e) => setMilestoneForm(prev => ({ ...prev, date: e.target.value }))}
              fullWidth
              required
              placeholder="e.g., Sep 15, Oct 20-25"
              helperText="Enter specific date or date range"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={milestoneForm.complete}
                  onChange={(e) => setMilestoneForm(prev => ({ ...prev, complete: e.target.checked }))}
                />
              }
              label="Mark as complete"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setMilestoneDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveMilestone}
            disabled={!milestoneForm.title || !milestoneForm.date}
          >
            {editingMilestone ? 'Save Changes' : 'Add Milestone'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Action Menu */}
      <Menu
        anchorEl={docMenuAnchor}
        open={Boolean(docMenuAnchor)}
        onClose={() => {
          setDocMenuAnchor(null);
          setPreviewDoc(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MenuItem onClick={() => {
          setPreviewDialog(true);
          setDocMenuAnchor(null);
        }}>
          <ListItemIcon>
            <PreviewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Preview</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          // In real app, would download the file
          console.log('Download:', previewDoc?.name);
          setDocMenuAnchor(null);
        }}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          // In real app, would open in new tab
          window.open(previewDoc?.url, '_blank');
          setDocMenuAnchor(null);
        }}>
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Open in New Tab</ListItemText>
        </MenuItem>
      </Menu>

      {/* Document Preview Dialog */}
      <Dialog
        open={previewDialog}
        onClose={() => {
          setPreviewDialog(false);
          setPreviewDoc(null);
        }}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            height: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {previewDoc?.type === 'pdf' && <PdfIcon sx={{ color: '#d32f2f' }} />}
            {previewDoc?.type === 'image' && <ImageIcon sx={{ color: '#1976d2' }} />}
            {previewDoc?.type === 'spreadsheet' && <SpreadsheetIcon sx={{ color: '#2e7d32' }} />}
            <Typography>{previewDoc?.name}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={() => window.open(previewDoc?.url, '_blank')}
              title="Open in new tab"
            >
              <OpenInNewIcon />
            </IconButton>
            <IconButton
              onClick={() => console.log('Download:', previewDoc?.name)}
              title="Download"
            >
              <DownloadIcon />
            </IconButton>
            <IconButton
              onClick={() => {
                setPreviewDialog(false);
                setPreviewDoc(null);
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {previewDoc?.type === 'pdf' && (
            <Box sx={{ width: '100%', height: '100%', bgcolor: 'grey.100', p: 2 }}>
              <iframe
                src={`${previewDoc.url}#toolbar=0`}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: 4,
                  backgroundColor: 'white'
                }}
                title={previewDoc.name}
              />
            </Box>
          )}
          {previewDoc?.type === 'image' && (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
              <img
                src={previewDoc.url}
                alt={previewDoc.name}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>
          )}
          {previewDoc?.type === 'spreadsheet' && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <SpreadsheetIcon sx={{ fontSize: 64, color: '#2e7d32', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {previewDoc.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Size: {previewDoc.size} • Modified: {previewDoc.date}
              </Typography>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{ mt: 2 }}
                onClick={() => console.log('Download spreadsheet:', previewDoc.name)}
              >
                Download Spreadsheet
              </Button>
              <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
                Spreadsheet preview requires download
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Activity Dashboard - ClickUp Style Full Screen */}
      <Dialog
        open={tasksExpanded}
        onClose={() => setTasksExpanded(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '95vw',
            height: '90vh',
            maxWidth: 'none',
            maxHeight: 'none',
            m: 2
          }
        }}
      >
        {/* Header */}
        <DialogTitle sx={{
          p: 3,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'grey.50'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ActivityIcon sx={{ color: 'info.main', fontSize: 28 }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {job?.jobNumber || 'J2024-087'}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                {job?.name || 'Noblesville High School Cameras'} • Task Management
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setTasksExpanded(false)}
            sx={{ bgcolor: 'background.paper' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Content - Full Task Dashboard */}
        <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <TaskDashboard jobId={job?.id || 'J2024-087'} />
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}